import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import {
  Linkedin,
  Facebook,
  AtSign,        // Threads (no dedicated icon in lucide)
  Instagram,
  Share2,
  Link2,
  Check,
} from 'lucide-react';

/**
 * Inline X/Twitter glyph (lucide doesn't ship a current X icon).
 */
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/**
 * Per-blog-post share row.
 *
 * Wires platform-specific share intents:
 *   • X / Twitter           → twitter.com/intent/tweet
 *   • Facebook              → www.facebook.com/sharer/sharer.php
 *   • LinkedIn              → linkedin.com/sharing/share-offsite
 *   • Threads               → threads.net/intent/post
 *   • Native share (mobile) → navigator.share() — opens the OS share sheet
 *                             which lets users post to Instagram Stories,
 *                             WhatsApp, Messenger, anything installed.
 *   • Copy link             → fallback for desktop / Instagram (Instagram
 *                             has no public web-share endpoint, so we tell
 *                             users to paste the link in their post)
 *
 * The OG image at /api/og/blog/{slug}.png is automatically picked up by
 * the receiving platform from the page's og:image tag, so each shared
 * preview shows the post's branded card.
 */
export const BlogShareRow = ({ post }) => {
  const [copied, setCopied] = useState(false);
  if (!post) return null;

  const slug = post.slug || post.post_id;
  const url = `https://clearcommunicationapp.com/blog/${slug}`;
  const title = post.title || 'ClearCommunicationApp';
  const excerpt = post.excerpt || title;
  const text = `${title} — ${excerpt}`;
  const enc = encodeURIComponent;

  // Fire-and-forget so a failed counter never blocks an actual share.
  const trackShare = (platform) => {
    apiFetch('/api/blog/track-share', {
      method: 'POST',
      body: { slug, platform },
    }).catch(() => {});
    import('@/lib/analytics').then(({ track }) => track.blogShare(slug, platform)).catch(() => {});
  };

  const intents = [
    {
      id: 'twitter',
      label: 'X (Twitter)',
      Icon: XIcon,
      href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}&via=clearcommapp`,
      brand: 'hover:bg-black hover:text-white',
    },
    {
      id: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      brand: 'hover:bg-[#1877F2] hover:text-white',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      brand: 'hover:bg-[#0A66C2] hover:text-white',
    },
    {
      id: 'threads',
      label: 'Threads',
      Icon: AtSign,
      href: `https://www.threads.net/intent/post?text=${enc(`${title} ${url}`)}`,
      brand: 'hover:bg-foreground hover:text-background',
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      trackShare('copy');
      toast.success('Link copied — paste it into Instagram or anywhere else.');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({ title, text, url });
      trackShare('native');
    } catch {
      // user cancelled — silent
    }
  };

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <section
      className="mt-8 not-prose"
      aria-label="Share this article"
      data-testid="blog-share-row"
    >
      <p className="text-sm font-semibold mb-2">Share this article</p>
      <div className="flex flex-wrap items-center gap-2">
        {intents.map(({ id, label, Icon, href, brand }) => (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${label}`}
            title={`Share on ${label}`}
            data-testid={`blog-share-${id}`}
            onClick={() => trackShare(id)}
            className={`h-10 w-10 rounded-full bg-muted flex items-center justify-center transition-colors ${brand}`}
          >
            <Icon className="h-4 w-4" />
          </a>
        ))}

        {/* Instagram — no public web share endpoint; fallback to copy link
            so users can paste it in a story / caption. */}
        <button
          type="button"
          onClick={() => { trackShare('instagram'); copyLink(); }}
          aria-label="Copy link to share on Instagram"
          title="Copy link (paste in Instagram Stories or caption)"
          data-testid="blog-share-instagram"
          className="h-10 w-10 rounded-full bg-muted flex items-center justify-center hover:bg-gradient-to-br hover:from-[#fd5949] hover:via-[#d6249f] hover:to-[#285AEB] hover:text-white transition-colors"
        >
          <Instagram className="h-4 w-4" />
        </button>

        {/* Native share sheet (mobile) */}
        {canNativeShare && (
          <Button
            variant="outline"
            size="sm"
            onClick={nativeShare}
            data-testid="blog-share-native"
            className="ml-1"
          >
            <Share2 className="h-4 w-4 mr-1" /> More
          </Button>
        )}

        {/* Copy link */}
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          data-testid="blog-share-copy"
          className="ml-auto sm:ml-1"
        >
          {copied ? (
            <><Check className="h-4 w-4 mr-1" /> Copied</>
          ) : (
            <><Link2 className="h-4 w-4 mr-1" /> Copy link</>
          )}
        </Button>
      </div>
    </section>
  );
};

export default BlogShareRow;
